import { useEffect, useState } from "react";
import { BarChart3, Mail } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatApiError } from "@/lib/api";
import { chip } from "@/components/site/OrderSpecFields";

const PERIODS = [["today", "Hoje"], ["7d", "7 dias"], ["30d", "30 dias"], ["all", "Tudo"]];
const SOURCE_LABELS = { aparelho: "Aparelho", oferta: "Oferta da semana", pedido: "Fazer pedido", simulador: "Simulador de parcelas", carrinho: "Carrinho", troca: "Avaliação de troca", geral: "Botão geral", outros: "Outros" };
const dayLabel = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <p className="text-xs text-zinc-400">{label}</p>
    <p data-testid={testid} className="font-display mt-1 text-2xl font-bold text-white">{value ?? 0}</p>
  </div>
);

const Ranking = ({ title, items, testid, empty }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-sm font-semibold text-white">{title}</p>
      {items.length === 0 && <p className="mt-3 text-sm text-zinc-400">{empty}</p>}
      <ul className="mt-3 space-y-2.5">
        {items.map((i) => (
          <li key={i.key} data-testid={`${testid}-item`}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-zinc-200">{i.label}</span>
              <span className="shrink-0 font-semibold text-white">{i.value}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-[#25D366]" style={{ width: `${(i.value / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const DailyChart = ({ daily }) => (
  <div data-testid="analytics-chart" className="h-48 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 168 }}>
      <BarChart data={daily} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
        <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} minTickGap={12} />
        <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: "rgba(255,255,255,0.06)" }} labelFormatter={dayLabel} formatter={(v) => [v, "Acessos"]}
          contentStyle={{ background: "#141416", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
        <Bar dataKey="visits" fill="#25D366" radius={[6, 6, 0, 0]} maxBarSize={36} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const ProductTable = ({ rows }) => (
  <div data-testid="analytics-top-products" className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:col-span-2">
    <p className="text-sm font-semibold text-white">Aparelhos: vistas e contatos pelo WhatsApp</p>
    {rows.length === 0 && <p className="mt-3 text-sm text-zinc-400">Nenhuma visualização ainda.</p>}
    {rows.length > 0 && (
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-zinc-400"><th className="pb-2 font-normal">Aparelho</th><th className="pb-2 text-right font-normal">Vistas</th><th className="pb-2 text-right font-normal">Contatos</th><th className="hidden pb-2 text-right font-normal sm:table-cell">Contato a cada 100 vistas</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.product_id} data-testid="analytics-top-products-item" className="border-t border-white/10">
              <td className="max-w-0 truncate py-2 pr-3 text-zinc-200">{r.model || "Aparelho removido"}</td>
              <td data-testid="analytics-product-views" className="py-2 text-right text-white">{r.views}</td>
              <td data-testid="analytics-product-contacts" className="py-2 text-right font-semibold text-[#25D366]">{r.contacts}</td>
              <td className="hidden py-2 text-right text-zinc-300 sm:table-cell">{r.views ? Math.round((r.contacts / r.views) * 100) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const SendReport = () => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/report/send");
      toast.success(`Resumo da semana enviado para ${data?.to || "seu e-mail"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" data-testid="analytics-send-report" onClick={send} disabled={busy}
      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 disabled:opacity-60">
      <Mail className="h-4 w-4" /> {busy ? "Enviando…" : "Enviar resumo da semana agora"}
    </button>
  );
};

export const AnalyticsPanel = () => {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/analytics", { params: { period } }).then((r) => alive && setData(r?.data || null)).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [period]);

  const sources = (data?.contacts_by_source || []).map((s) => ({ key: s.source, label: SOURCE_LABELS[s.source] || s.source, value: s.count }));
  const products = Array.isArray(data?.top_products) ? data.top_products : [];
  const terms = (data?.top_searches || []).map((t) => ({ key: t.term, label: t.term, value: t.count }));

  return (
    <section data-testid="admin-analytics" className="mt-8 rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <BarChart3 className="h-5 w-5 text-white" />
          <h2 className="font-display text-xl font-bold">Estatísticas do site</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map(([id, label]) => (
            <button key={id} type="button" data-testid={`analytics-period-${id}`} aria-pressed={period === id} onClick={() => setPeriod(id)} className={chip(period === id)}>{label}</button>
          ))}
        </div>
      </div>
      <p className="mt-2 text-sm text-zinc-400">Dados anônimos. Suas visitas não contam enquanto você estiver logado neste aparelho. Toda segunda às 12h você recebe o resumo da semana por e-mail.</p>
      <div className="mt-3"><SendReport /></div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Acessos" value={data?.visits} testid="analytics-visits" />
        <Stat label="Visitantes únicos" value={data?.visitors} testid="analytics-visitors" />
        <Stat label="Aparelhos vistos" value={data?.views} testid="analytics-views" />
        <Stat label="Pesquisas" value={data?.searches} testid="analytics-searches" />
        <Stat label="Contatos WhatsApp" value={data?.contacts} testid="analytics-contacts" />
      </div>
      <div className="mt-3"><DailyChart daily={Array.isArray(data?.daily) ? data.daily : []} /></div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <ProductTable rows={products} />
        <Ranking title="Contatos por origem" items={sources} testid="analytics-sources" empty="Nenhum contato ainda." />
        <Ranking title="Mais pesquisados" items={terms} testid="analytics-top-searches" empty="Nenhuma pesquisa ainda." />
      </div>
    </section>
  );
};
